/**
 * `SkillPreviewModal` — ported from `Foyer_Dashboard_CURRENT.jsx` line 1483.
 * Read-only preview of a skill's request / response shape.
 */

import { Lock } from 'lucide-react';

import type { Skill } from '@sill/types';

import { useC } from '../../lib/theme.js';
import { Card, Modal, Pill } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface SkillPreviewModalProps {
  skill: Skill | null;
  onClose: () => void;
}

const SAMPLES: Record<string, string> = {
  browse_catalog: '{ "query": "espresso", "max_price": 25 }',
  place_order: '{ "items": [{ "sku": "esp_12oz", "qty": 1 }] }',
  request_refund: '{ "order_id": "ord_abc123", "reason": "damaged" }',
};

export function SkillPreviewModal({ skill, onClose }: SkillPreviewModalProps): JSX.Element | null {
  const C = useC();
  if (!skill) return null;
  const sample = SAMPLES[skill.skill_id] ?? '{ ... }';

  return (
    <Modal open={!!skill} onClose={onClose} size="md" title={skill.name} subtitle={skill.skill_id}>
      <div className="p-6 space-y-4">
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1.5,
              marginBottom: 6,
            }}
          >
            DESCRIPTION
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6 }}>
            {skill.description}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {skill.auth_required && (
            <Pill tone="amber" icon={Lock}>
              REQUIRES SIGNED MANDATE
            </Pill>
          )}
          <Pill tone={skill.enabled ? 'lime' : 'neutral'}>
            {skill.enabled ? 'ENABLED' : 'DISABLED'}
          </Pill>
        </div>
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1.5,
              marginBottom: 6,
            }}
          >
            SAMPLE REQUEST
          </div>
          <Card className="p-3">
            <pre
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.text,
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {`POST /a2a/messages
{
  "skill": "${skill.skill_id}",
  "args": ${sample}${skill.auth_required ? ',\n  "mandate": "<AP2 signed>"' : ''}
}`}
            </pre>
          </Card>
        </div>
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1.5,
              marginBottom: 6,
            }}
          >
            EXPECTED RESPONSE
          </div>
          <Card className="p-3">
            <pre
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.text,
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {`200 OK
{
  "ok": true,
  "result": { ... }${skill.auth_required ? ',\n  "mandate_verified": true' : ''}
}`}
            </pre>
          </Card>
        </div>
      </div>
      <div
        className="px-6 py-4 flex items-center gap-2"
        style={{ borderTop: `1px solid ${C.border}` }}
      >
        <button
          type="button"
          onClick={onClose}
          className="ml-auto px-4 py-2"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.muted,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
